/**
 * Global "critical operation in flight" counter.
 *
 * Used by AuthProvider to know when NOT to reload the page or invalidate
 * queries. The core case: a photo upload can take 5-15s on a slow mobile
 * link, and if the auth self-heal reload fires during that window the File
 * (which only lives in memory as a `<input type=file>` result) is destroyed
 * mid-request and the photo is lost. Every long-running interactive write
 * that would be catastrophic to interrupt should wrap itself with
 * beginCriticalOp() / the returned end().
 *
 * Intentionally simple — no persistence, no cross-tab sync, no timeout. If
 * a caller forgets to end() we'd stall refreshes forever; every caller MUST
 * use `try { ... } finally { end() }`.
 */

let count = 0
const listeners = new Set<() => void>()

function notify() {
  for (const cb of listeners) {
    try {
      cb()
    } catch (err) {
      console.warn('critical-ops listener threw', err)
    }
  }
}

/**
 * Bump the counter. Returns the corresponding `end` function — call it in a
 * `finally` block, guaranteed once. Calling end() more than once is a no-op.
 */
export function beginCriticalOp(): () => void {
  count += 1
  notify()
  let ended = false
  return () => {
    if (ended) return
    ended = true
    count = Math.max(0, count - 1)
    notify()
  }
}

/** Current in-flight count. */
export function getCriticalOpCount(): number {
  return count
}

/** True while at least one critical op is in flight. */
export function hasCriticalOpsInFlight(): boolean {
  return count > 0
}

/** Subscribe to count changes. Returns an unsubscribe function. */
export function subscribeCriticalOps(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/**
 * Convenience: wrap an async function so the op is auto-ended.
 *   const url = await withCriticalOp(() => uploadFile(...))
 */
export async function withCriticalOp<T>(fn: () => Promise<T>): Promise<T> {
  const end = beginCriticalOp()
  try {
    return await fn()
  } finally {
    end()
  }
}
